import React, { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { OpenModalHookType } from '@hooks/useOpenModal';
import Calculator from '@components/Utilities/Calculator';
import { SalesChannel, PaymentMethods } from '@api/SalesChannels/SalesChannel';
import { currencyFormatter } from '@utils/Numbers';

// Cobro con LA CALCULADORA de siempre + selector del tipo de pago (rediseño QA
// 2026-08-26, 2ª ronda — el equipo trabaja con el keypad, no con campos de
// texto). Multipago: capturar Tarjeta/Transferencia registra ese pago, se
// DESCUENTA en el resumen del pedido (OrderTotals, visible detrás) y el
// restante se actualiza aquí; el Efectivo es el residuo — se captura lo
// recibido, se muestra el cambio y "Ingresar pedido" cierra la venta. Si
// tarjeta/transferencia cubren todo, el botón se habilita sin efectivo.
type PaymentType = 'cash' | 'card' | 'transfer';

interface ModalPagoProps {
    modalPago: OpenModalHookType;
    // Restante a cobrar = total + propina − tarjeta − transferencia − efectivo registrado
    remaining: number;
    // Limita los métodos disponibles (misma regla que los botones viejos)
    salesChannel?: SalesChannel;
    // PAGO LIBRE (QA 2026-08-26): también el EFECTIVO se registra en
    // parcialidades — $50 en efectivo y el resto con tarjeta, o al revés,
    // o tres pagos; cualquier combinación en cualquier orden.
    onRegisterPayment: (type: 'cash' | 'card' | 'transfer', amount: number) => void;
    onContinue: () => void;
}

export const ModalPago: React.FunctionComponent<ModalPagoProps> = (props) => {
    const { modalPago, remaining, salesChannel, onRegisterPayment, onContinue } = props;
    const [paymentType, setPaymentType] = useState<PaymentType>('cash');
    const [amount, setAmount] = useState<number>(0);
    // "Monto total": fija el restante y apaga el keypad (patrón del checkbox
    // del ModalCreditCardCalculator viejo)
    const [exactAmount, setExactAmount] = useState<boolean>(false);
    // La Calculator guarda su valor adentro: remontarla (key) es el reset
    const [calculatorKey, setCalculatorKey] = useState<number>(0);

    const canPayCard =
        !salesChannel || (salesChannel && salesChannel.paymentMethods.includes(PaymentMethods.creditCard));
    const canPayTransfer =
        !salesChannel || (salesChannel && salesChannel.paymentMethods.includes(PaymentMethods.bankTransfer));

    useEffect(() => {
        if (!modalPago.modalOpen) return;
        setPaymentType('cash');
        setAmount(0);
        setCalculatorKey((key) => key + 1);
    }, [modalPago.modalOpen]);

    const resetAmount = () => {
        setAmount(0);
        setExactAmount(false);
        setCalculatorKey((key) => key + 1);
    };

    const handleClose = () => {
        resetAmount();
        modalPago.toggle();
    };

    const onChangeType = (_: unknown, value: PaymentType | null) => {
        if (!value) return;
        setPaymentType(value);
        resetAmount();
    };

    const change = paymentType === 'cash' && amount > remaining ? amount - remaining : 0;
    const isCash = paymentType === 'cash';
    // Un pago registrado nunca supera el restante (tarjeta/transferencia por el
    // monto exacto SÍ completan); el efectivo que llega al restante o lo supera
    // es el pago FINAL — de ahí sale el cambio.
    const canRegister = amount > 0 && (isCash ? amount < remaining : amount <= remaining);
    const canFinish = remaining === 0 || (isCash && amount >= remaining && remaining > 0);

    const handleRegister = () => {
        if (!canRegister) return;
        onRegisterPayment(paymentType, amount);
        // El restante nuevo se ve al instante y el resumen del pedido (atrás)
        // ya muestra el pago registrado
        resetAmount();
    };

    const handleContinue = () => {
        handleClose();
        onContinue();
    };

    return (
        <Dialog
            PaperProps={{ style: { borderRadius: 15, padding: 4 } }}
            open={modalPago.modalOpen}
            onClose={handleClose}
            aria-labelledby="modal-pago-title"
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle id="modal-pago-title">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                        Cobrar
                    </Typography>
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack alignItems="center" spacing={2}>
                    <ToggleButtonGroup
                        fullWidth
                        exclusive
                        color="primary"
                        value={paymentType}
                        onChange={onChangeType}
                    >
                        <ToggleButton value="cash">Efectivo</ToggleButton>
                        {canPayCard && <ToggleButton value="card">Tarjeta</ToggleButton>}
                        {canPayTransfer && <ToggleButton value="transfer">Transferencia</ToggleButton>}
                    </ToggleButtonGroup>
                    <Typography>{`Restante a cobrar: ${currencyFormatter(remaining)}`}</Typography>
                    <Typography variant="h3" fontWeight={600}>
                        {currencyFormatter(amount)}
                    </Typography>
                    {isCash ? (
                        <Typography variant="body1">{`Cambio: ${change.toFixed(2)}`}</Typography>
                    ) : (
                        <Button
                            size="small"
                            variant={exactAmount ? 'contained' : 'outlined'}
                            onClick={() => {
                                if (exactAmount) {
                                    resetAmount();
                                } else {
                                    setExactAmount(true);
                                    setAmount(remaining);
                                }
                            }}
                        >
                            Monto total ({currencyFormatter(remaining)})
                        </Button>
                    )}
                    <Calculator
                        key={calculatorKey}
                        showAmount={false}
                        disabled={exactAmount}
                        onChangeValue={setAmount}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Stack direction="row" spacing={2} width="100%">
                    <Button
                        disabled={!canRegister}
                        fullWidth
                        onClick={handleRegister}
                        color="primary"
                        variant="outlined"
                    >
                        {`Registrar ${isCash ? 'efectivo' : paymentType === 'card' ? 'tarjeta' : 'transferencia'}`}
                    </Button>
                    <Button
                        disabled={!canFinish}
                        fullWidth
                        onClick={handleContinue}
                        color="success"
                        variant="contained"
                    >
                        Ingresar pedido
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
